import { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
// Import Material UI components
import {
  Box, Grid, Paper, Typography, Accordion, AccordionSummary, AccordionDetails,
  useTheme, Card, CardContent, Tooltip, Zoom, Fade,
  Skeleton
} from '@mui/material';
// Import Material UI icons
import {
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon,
  Lightbulb as LightbulbIcon,
  QuestionAnswer as QuestionAnswerIcon,
  EmojiObjects as EmojiObjectsIcon
} from '@mui/icons-material';

const MemberHelpPage = () => {
  const { tenant } = useTenant();
  const theme = useTheme();
  const [mascotTip, setMascotTip] = useState(null);
  const [mascotVisible, setMascotVisible] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(false);

  // Mascot tips that will display randomly
  const mascotTips = [
    "Did you know? You can access your digital membership card from your dashboard!",
    "Tip: Keeping your profile up-to-date helps us serve you better!",
    "Quick Tip: Bookmark your dashboard page for faster access next time!",
    `${tenant?.name || 'Membership'} fact: Our community has been growing since ${tenant?.createdAt ? new Date(tenant.createdAt).getFullYear() : '2023'}!`,
    "Need a shortcut? Press '/' on your keyboard to access search from anywhere!",
    "May 1st, 2025 update: We've added new features to your membership portal!",
  ];

  // Show a random tip every 20 seconds
  useEffect(() => {
    // Initial tip display after 3 seconds
    const initialTimer = setTimeout(() => {
      setMascotTip(mascotTips[Math.floor(Math.random() * mascotTips.length)]);
    }, 3000);

    // Set up interval for changing tips
    const interval = setInterval(() => {
      setMascotTip(null);
      // Hide tip for 1 second before showing new one
      setTimeout(() => {
        setMascotTip(mascotTips[Math.floor(Math.random() * mascotTips.length)]);
      }, 1000);
    }, 20000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [tenant]);

  // Mascot animation on FAQ expansion
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded);
    if (isExpanded) {
      // Show an encouraging message when user expands an FAQ
      setMascotTip("Great question! Hope this helps you out!");
      // Reset after 5 seconds
      setTimeout(() => {
        setMascotTip(mascotTips[Math.floor(Math.random() * mascotTips.length)]);
      }, 5000);
    }
  };

  // Common FAQs
  const faqs = [
    {
      question: "How do I update my profile information?",
      answer: "You can update your profile information by navigating to the Profile page from the main menu. Click on the 'Edit Profile' button to make changes to your personal information."
    },
    {
      question: "How can I view my membership status?",
      answer: "Your membership status is displayed on your dashboard when you log in. It shows details like your current status, membership number, and renewal date."
    },
    {
      question: "What should I do if I forgot my password?",
      answer: "Click the 'Forgot Password' link on the login page. Enter your email address, and we'll send you instructions to reset your password."
    },
    {
      question: "How can I renew my membership?",
      answer: "To renew your membership, visit the Membership page and select the 'Renew' option. You'll be guided through the renewal process, including payment options."
    },
    {
      question: "Where can I find my membership card?",
      answer: "Your digital membership card is available on your dashboard. You can view it and download it for printing if needed."
    },
    {
      question: "How do I update my payment information?",
      answer: "Go to your Account Settings and select 'Payment Methods' to update your credit card information or change your preferred payment method."
    }
  ];

  // Primary color with fallback
  const primaryColor = tenant?.primaryColor || theme.palette.primary.main;
  const gradientBackground = `linear-gradient(135deg, ${primaryColor} 0%, ${theme.palette.mode === 'dark' ? darkenColor(primaryColor, 0.3) : lightenColor(primaryColor, 0.3)} 100%)`;

  // Mascot style based on primary color
  const mascotStyle = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: gradientBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
    transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    zIndex: 10,
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    },
    animation: mascotVisible ? 'none' : 'pulse 2s infinite'
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      {/* Hero Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 3, 
          p: { xs: 2, sm: 3 }, 
          borderRadius: 3,
          background: gradientBackground,
          color: 'white',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <Box sx={{ 
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '100%',
          opacity: 0.1,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="%23ffffff" fill-opacity="1" fill-rule="evenodd"/%3E%3C/svg%3E")',
          backgroundSize: 'cover',
          zIndex: 0
        }} />
        
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <HelpIcon sx={{ fontSize: 36, mr: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Help Center
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 1, maxWidth: '80%' }}>
            Get assistance with your {tenant?.name || 'membership'} account
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: '70%' }}>
            Browse our frequently asked questions to find answers to common questions.
          </Typography>
        </Box>
      </Paper>
      
      {/* Quick Help Section */}
      <Card sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ 
          p: 2, 
          bgcolor: 'rgba(103,58,183,0.04)', 
          borderBottom: '1px solid rgba(0,0,0,0.09)',
          display: 'flex',
          alignItems: 'center'
        }}>
          <LightbulbIcon sx={{ color: 'warning.main', mr: 1 }} />
          <Typography variant="h6" color="text.primary" fontWeight="medium">
            Quick Help Resources
          </Typography>
        </Box>
        
        <CardContent>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: 'rgba(25, 118, 210, 0.04)', 
              border: '1px solid rgba(25, 118, 210, 0.15)'
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
              <QuestionAnswerIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'text-bottom' }} />
              Common Questions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Browse our frequently asked questions section below to find quick answers to common inquiries.
            </Typography>
          </Paper>
        </CardContent>
      </Card>

      {/* FAQs Section */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          height: '100%'
        }}
      >
        <Box 
          sx={{ 
            px: 3, 
            py: 2, 
            borderBottom: 1, 
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <HelpIcon sx={{ color: primaryColor, mr: 1 }} />
          <Typography variant="h6" fontWeight="bold" color="text.primary">
            Frequently Asked Questions
          </Typography>
        </Box>
        
        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
          {faqs.map((faq, index) => (
            <Accordion 
              key={index} 
              elevation={0}
              disableGutters
              onChange={handleAccordionChange(`panel${index}`)}
              sx={{
                mb: 1,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.08)',
                '&.Mui-expanded': {
                  borderLeft: `3px solid ${primaryColor}`,
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }
                }}
              >
                <Typography sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'rgba(0,0,0,0.01)' }}>
                <Typography sx={{ color: 'text.secondary' }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Paper>
      
      {/* Brand Mascot */}
      <Tooltip
        title={mascotTip || "Hello! I'm your helper. Click me for tips!"}
        placement="left"
        arrow
        open={!!mascotTip}
        TransitionComponent={Zoom}
        PopperProps={{
          sx: {
            '& .MuiTooltip-tooltip': {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              padding: '12px 16px',
              borderRadius: '12px',
              maxWidth: '280px',
              border: `1px solid ${primaryColor}20`,
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
            },
            '& .MuiTooltip-arrow': {
              color: theme.palette.background.paper,
            },
          }
        }}
      >
        <Box
          sx={mascotStyle}
          onClick={() => {
            setMascotVisible(!mascotVisible);
            if (!mascotVisible) {
              setMascotTip(mascotTips[Math.floor(Math.random() * mascotTips.length)]);
            } else {
              setMascotTip(null);
            }
          }}
        >
          <EmojiObjectsIcon 
            sx={{
              color: 'white',
              fontSize: '28px',
              animation: expandedFaq ? 'bounce 0.5s' : 'none'
            }}
          />
        </Box>
      </Tooltip>
      
      {/* Global styles for animations */}
      <style jsx global>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }
      `}</style>
    </Box>
  );
};

// Helper functions for color manipulation
function lightenColor(color, factor) {
  if (!color) return '#e0e0e0';
  
  // Convert hex to RGB
  let r, g, b;
  if (color.startsWith('#')) {
    r = parseInt(color.slice(1, 3), 16);
    g = parseInt(color.slice(3, 5), 16);
    b = parseInt(color.slice(5, 7), 16);
  } else {
    return color; // Not a hex color
  }
  
  // Lighten
  r = Math.min(255, r + (255 - r) * factor);
  g = Math.min(255, g + (255 - g) * factor);
  b = Math.min(255, b + (255 - b) * factor);
  
  // Convert back to hex
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

function darkenColor(color, factor) {
  if (!color) return '#424242';
  
  // Convert hex to RGB
  let r, g, b;
  if (color.startsWith('#')) {
    r = parseInt(color.slice(1, 3), 16);
    g = parseInt(color.slice(3, 5), 16);
    b = parseInt(color.slice(5, 7), 16);
  } else {
    return color; // Not a hex color
  }
  
  // Darken
  r = Math.max(0, r * (1 - factor));
  g = Math.max(0, g * (1 - factor));
  b = Math.max(0, b * (1 - factor));
  
  // Convert back to hex
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

export default MemberHelpPage;
